import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { PinKeypad } from '@/components/ui/pin-keypad';
import { normalizeNameToId } from '@/lib/normalize-name';
import { LogIn, Loader2, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import villatechBlueLogo from '@/assets/villatech-logo-blue.png';

const MAX_PIN_LENGTH = 6;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [step, setStep] = useState<'username' | 'pin'>('username');
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isError, setIsError] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  

  // Redirect if already logged in (unless user explicitly navigated here)
  // Note: Only redirect if we weren't just logging in (step is still 'username')
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const forceShow = searchParams.get('force') === '1';
    
    // Only redirect if user is already logged in BEFORE attempting login
    // (step === 'username' means user hasn't started the login flow yet)
    if (user && !authLoading && !forceShow && step === 'username' && !loading) {
      // Check if user is admin or vendedor and redirect accordingly
      const checkRoleAndRedirect = async () => {
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .in('role', ['admin', 'vendedor']);

        const roles = rolesData?.map(r => r.role) || [];
        const hasAdminAccess = roles.includes('admin') || roles.includes('vendedor');

        const defaultRoute = hasAdminAccess ? '/admin/canjes' : '/mi-membresia';
        const from = (location.state as any)?.from?.pathname || defaultRoute;
        navigate(from, { replace: true });
      };
      checkRoleAndRedirect();
    }
  }, [user, authLoading, navigate, location, step, loading]);

  const handleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username.trim()) {
      toast({
        title: 'Campo requerido',
        description: 'Por favor ingresa tu nombre completo',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const input = username.trim().replace(/\s+/g, ' ');

      const { data: profilesRaw, error } = await (supabase as any).rpc('find_profile_for_login', {
        _nombre: input,
      });

      const profiles = (profilesRaw ?? []) as Array<{
        user_id: string;
        nombre: string;
        username: string | null;
      }>;

      if (error || profiles.length === 0) {
        toast({
          title: 'Usuario no encontrado',
          description: 'No se encontró una cuenta con ese nombre',
          variant: 'destructive',
        });
        return;
      }

      // If multiple results, find the best match by normalized name
      const inputNormalized = normalizeNameToId(input);
      const inputNormalizedCollapsed = inputNormalized.replace(/_/g, '');

      const profile =
        profiles.find((p) => {
          const norm = normalizeNameToId(p.nombre);
          return norm === inputNormalized || norm.replace(/_/g, '') === inputNormalizedCollapsed;
        }) || profiles[0];

      // Use existing username if available (legacy), otherwise normalize the name
      const emailId = (profile.username && profile.username.trim()) || normalizeNameToId(profile.nombre);
      setUserEmail(`${emailId}@villatech.member`);
      setStep('pin');
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Ocurrió un error al buscar el usuario',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNumberClick = (num: string) => {
    if (pin.length < MAX_PIN_LENGTH) {
      setPin(prev => prev + num);
      setIsError(false);
    }
  };

  const handleDelete = () => {
    setPin(prev => prev.slice(0, -1));
    setIsError(false);
  };

  const verifyPin = async () => {
    setIsVerifying(true);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password: pin,
      });

      if (error) {
        setIsError(true);
        setIsVerifying(false);
        toast({
          title: 'PIN incorrecto',
          description: 'El PIN ingresado no es válido',
          variant: 'destructive',
        });
        
        setTimeout(() => {
          setPin('');
          setIsError(false);
        }, 1000);
        return;
      }

      // Check if user is admin or vendedor
      const { data: session } = await supabase.auth.getSession();
      const userId = session.session?.user?.id;
      
      let isAdmin = false;
      let isVendedor = false;
      if (userId) {
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);
        
        const roles = roleData?.map(r => r.role) || [];
        isAdmin = roles.includes('admin');
        isVendedor = roles.includes('vendedor');
      }

      const hasAdminAccess = isAdmin || isVendedor;

      toast({
        title: '¡Bienvenido!',
        description: isAdmin ? 'Acceso de administrador' : isVendedor ? 'Acceso de vendedor' : 'Has iniciado sesión correctamente',
      });

      const defaultRoute = hasAdminAccess ? '/admin/canjes' : '/mi-membresia';
      const from = (location.state as any)?.from?.pathname || defaultRoute;
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 800);
    } catch (err) {
      setIsError(true);
      setIsVerifying(false);
      toast({
        title: 'Error',
        description: 'Ocurrió un error inesperado',
        variant: 'destructive',
      });
      setTimeout(() => {
        setPin('');
        setIsError(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (pin.length === MAX_PIN_LENGTH && !isVerifying && step === 'pin') {
      verifyPin();
    }
  }, [pin, isVerifying, step]);

  const handleBack = () => {
    setStep('username');
    setPin('');
    setIsError(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-start justify-center pt-8 sm:pt-16 md:pt-24 p-4">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        {/* Login card */}
        <div className="bg-zinc-900/80 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-zinc-800 shadow-2xl">
          {/* Navigation inside card */}
          <div className="mb-4">
            {step === 'username' ? (
              <Link 
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Link>
            ) : (
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Cambiar usuario
              </button>
            )}
          </div>

          {/* Logo */}
          <div className="text-center mb-6">
            <img 
              src={villatechBlueLogo} 
              alt="VillaTech" 
              className="h-12 md:h-14 mx-auto mb-4"
            />
            <h1 className="text-xl md:text-2xl font-bold text-white">
              {step === 'username' ? 'Iniciar Sesión' : 'Ingresa tu PIN'}
            </h1>
            {step === 'pin' && (
              <p className="text-zinc-400 mt-2 text-sm md:text-base">
                Hola, {username}
              </p>
            )}
          </div>

          <AnimatePresence mode="wait">
            {step === 'username' ? (
              <motion.form 
                key="username-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleUsernameSubmit} 
                className="space-y-6"
              >
                {/* Name field */}
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-zinc-300">
                    Nombre y Primer Apellido
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Ej: Roberto Carrillo"
                    className="bg-zinc-800/80 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500/20"
                    disabled={loading}
                    autoFocus
                  />
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-6 rounded-xl shadow-lg shadow-blue-500/20"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="w-5 h-5 mr-2" />
                      Continuar
                    </>
                  )}
                </Button>
              </motion.form>
            ) : (
              <motion.div
                key="pin-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {/* PIN Display */}
                <div className="mb-4">
                  <div className="flex justify-center gap-2 mb-2">
                    {[...Array(MAX_PIN_LENGTH)].map((_, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.05 * idx }}
                        className={`w-10 h-10 md:w-12 md:h-12 rounded-xl border-2 flex items-center justify-center transition-all ${
                          isError
                            ? 'border-red-500 bg-red-500/10 animate-shake'
                            : pin.length > idx
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'border-zinc-700 bg-zinc-800/50'
                        }`}
                      >
                        {pin.length > idx && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2.5 h-2.5 rounded-full bg-blue-400"
                          />
                        )}
                      </motion.div>
                    ))}
                  </div>

                  {/* Status Indicator */}
                  <AnimatePresence mode="wait">
                    {isVerifying && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="text-center text-sm text-white/60 mt-4"
                      >
                      <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          Verificando...
                        </div>
                      </motion.div>
                    )}
                    
                    {pin.length === MAX_PIN_LENGTH && !isVerifying && !isError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="text-center text-sm text-green-400 mt-4 flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Acceso concedido
                      </motion.div>
                    )}

                    {isError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="text-center text-sm text-red-400 mt-4 flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                        PIN incorrecto
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Keypad */}
                <PinKeypad
                  onNumberClick={handleNumberClick}
                  onDelete={handleDelete}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Sign up link */}
          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              ¿No tienes una membresía?{' '}
              <button 
                onClick={() => {
                  navigate('/');
                  setTimeout(() => {
                    document.getElementById('planes')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                Obtén la tuya aquí
              </button>
            </p>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

export default Login;
